<script lang="ts" setup>
import SingleSelect from '~/components/form-elements/SingleSelect.vue'
import menuItems from '~/constants/menu-items'
import { CategoryType } from '~/enums/category-type'
import Page from '~/enums/page'
import helpers from '~/helpers'
import type { BrandData } from '~/types/brands'
import type { AutocompleteItem } from '~/types/form-elements'

const props = defineProps<{
  brandsData: BrandData[]
  categoriesData: any[]
}>()

const placeholder = 'Выберите из списка'

// Active ingredients
const activeIngredientQuery = ref<string | null>(null)
const autocompleteActiveIngredients = ref<AutocompleteItem[]>([])
const selectedActiveIngredient = ref<any>(null)

// Body systems
const autocompleteBodySystems = ref<AutocompleteItem[]>([])
const bodySystemQuery = ref<string | null>(null)
const selectedBodySystem = ref<any>(null)

// Brands
const autocompleteBrands = ref<AutocompleteItem[]>([])
const brandQuery = ref<string | null>(null)
const selectedBrand = ref<any>(null)

const activeIngredientOptions = computed(
  () => props
    .categoriesData
    .filter((x: any): any => x.attributes.typeId === CategoryType.ActiveIngredient)
    .map((x: any): any => ({ id: x.id, name: x.attributes.name, path: helpers.getCategoryPath(x) }))
)
const bodySystemOptions = computed(
  () => props
    .categoriesData
    .filter((x: any): any => x.attributes.typeId === CategoryType.BodySystem)
    .map((x: any): any => ({ id: x.id, name: x.attributes.name, path: helpers.getCategoryPath(x) }))
)
const brandOptions = computed(
  () => props.brandsData.map((x: any): any => ({ id: x.id, name: x.attributes.name, path: helpers.getBrandPath(x) }))
)

watch(activeIngredientQuery, (value: string): void => {
  autocompleteActiveIngredients.value = value
    ? activeIngredientOptions.value.filter((x: any): boolean => x.name.toLowerCase().includes(value.toLowerCase()))
    : activeIngredientOptions.value
})
watch(bodySystemQuery, (value: string): void => {
  autocompleteBodySystems.value = value
    ? bodySystemOptions.value.filter((x: any): boolean => x.name.toLowerCase().includes(value.toLowerCase()))
    : bodySystemOptions.value
})
watch(brandQuery, (value: string): void => {
  autocompleteBrands.value = value
    ? brandOptions.value.filter((x: any): boolean => x.name.toLowerCase().includes(value.toLowerCase()))
    : brandOptions.value
})

onMounted(() => {
  autocompleteActiveIngredients.value = activeIngredientOptions.value
  autocompleteBodySystems.value = bodySystemOptions.value
  autocompleteBrands.value = brandOptions.value
})

async function updateSelectedActiveIngredient (x: any, isAdding: boolean): Promise<void> {
  selectedActiveIngredient.value = x

  if (isAdding) {
    autocompleteActiveIngredients.value = []
    await navigateTo(x.path)
  }
}

async function updateSelectedBodySystem (x: any, isAdding: boolean): Promise<void> {
  selectedBodySystem.value = x

  if (isAdding) {
    autocompleteBodySystems.value = []
    await navigateTo(x.path)
  }
}

async function updateSelectedBrand (x: any, isAdding: boolean): Promise<void> {
  selectedBrand.value = x

  if (isAdding) {
    autocompleteBrands.value = []
    await navigateTo(x.path)
  } else {
    await navigateTo(Page.Search)
  }
}
</script>

<template>
  <section>
    <div class="container catalog-menu-container">
      <div class="catalog-menu">
        <div class="row wide mb-4">
          <div class="column-4 tb12 with-margin-bottom-16">
            <SingleSelect
              v-model="activeIngredientQuery"
              :autocomplete-items="autocompleteActiveIngredients"
              is-label-text-white
              label-text="Активное вещество"
              light-green-border
              :placeholder="placeholder"
              :selected-item="selectedActiveIngredient"
              show-arrow
              @update-selected-item="updateSelectedActiveIngredient"
            />
          </div>
          <div class="column-4 tb12 with-margin-bottom-16">
            <SingleSelect
              v-model="bodySystemQuery"
              :autocomplete-items="autocompleteBodySystems"
              is-label-text-white
              label-text="Система организма"
              light-green-border
              :placeholder="placeholder"
              :selected-item="selectedBodySystem"
              show-arrow
              @update-selected-item="updateSelectedBodySystem"
            />
          </div>
          <div class="column-4 tb12">
            <SingleSelect
              v-model="brandQuery"
              :autocomplete-items="autocompleteBrands"
              is-label-text-white
              label-text="Бренд"
              light-green-border
              :placeholder="placeholder"
              :selected-item="selectedBrand"
              show-arrow
              @update-selected-item="updateSelectedBrand"
            />
          </div>
        </div>

        <ul class="horizontal-menu -mb-2 flex flex-wrap">
          <li v-for="x in menuItems.catalog" :key="x.path" class="mb-2 mr-2 flex whitespace-nowrap" :class="x.class">
            <NuxtLink :to="x.path">
              <span>{{ x.name }}</span>
            </NuxtLink>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.catalog-menu {
  background: variables.$main-green-color;
  border-radius: 12px;
  padding: 16px;

  @media screen and (max-width: 600px) {
    border-radius: 0;
  }
}

.horizontal-menu {
  li {
    &:last-child {
      margin-right: 0;
    }

    &.black-friday > a {
      background: black;
      color: white;

      &:hover,
      &.nuxt-link-exact-active {
        background: variables.$main-gray-color;
        color: white;
      }
    }

    &.sale1111 > a {
      background: #ff9305;
      color: #4a2b0d;
      //color: white;

      &:hover,
      &.nuxt-link-exact-active {
        background: color.adjust(#ff9305, $lightness: 10%);
        color: white;
      }
    }

    & > a {
      background: white;
      border-radius: 12px;
      color: inherit;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      padding: 10px 16px;
      transition: background-color .25s ease, color .25s ease;

      &:hover,
      &.nuxt-link-exact-active {
        color: variables.$main-green-color;
        text-decoration: none;
      }
    }
  }
}
</style>
